import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaJsonLd from '@/components/base/SchemaJsonLd';
import { categories, allMovies } from '@/mocks/movies';

const categoryTabMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function Category() {
  const { category } = useParams<{ category: string }>();
  const [activeCategory, setActiveCategory] = useState(category || 'movie');
  const currentCat = categoryTabMap[activeCategory] || '电影';

  const filteredMovies = allMovies.filter((m) => m.category === activeCategory);

  useEffect(() => {
    document.title = `${currentCat} - 屁屁影院 | 免费在线观看${currentCat}`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `屁屁影院${currentCat}频道，提供海量高清${currentCat}资源，无需注册免费观看。每日更新最新${currentCat}，让您随时随地享受优质观影体验。`);
  }, [activeCategory, currentCat]);

  const breadcrumbs = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://pipicinema.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: currentCat,
          item: `https://pipicinema.com/category/${activeCategory}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${currentCat} - 屁屁影院`,
      description: `屁屁影院${currentCat}频道，提供海量高清${currentCat}资源，无需注册免费观看。`,
      url: `https://pipicinema.com/category/${activeCategory}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '屁屁影院',
        url: 'https://pipicinema.com/',
      },
    },
  ];

  return (
    <>
      <SchemaJsonLd schemas={breadcrumbs} />
      <Navbar />
      <Breadcrumb />

      <main className="min-h-[80vh] px-4 md:px-6 max-w-[1400px] mx-auto pb-16">
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
            影视分类
          </h1>

          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </header>

        <p className="text-sm text-foreground-500 mb-4">
          当前分类：<strong className="text-foreground-900">{currentCat}</strong> · 共 {filteredMovies.length} 部作品
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
          {filteredMovies.map((movie) => (
            <Link
              key={movie.id}
              to={`/article/${movie.id}`}
              className="group cursor-pointer"
            >
              <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-3">
                <img
                  src={movie.image}
                  alt={`屁屁影院 - ${movie.title}`}
                  title={`屁屁影院${movie.title}在线观看`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-2 right-2">
                  <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                    {movie.rating}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-xs leading-relaxed line-clamp-3">
                    {movie.description || ''}
                  </p>
                </div>
              </div>
              <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                {movie.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-foreground-500 bg-background-200/70 px-2 py-0.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                <span className="text-xs text-foreground-400 ml-auto">
                  {movie.year}
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filteredMovies.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-emotion-unhappy-line text-4xl text-foreground-300 mb-4"></i>
            <p className="text-foreground-500">暂无该分类内容，敬请期待更新</p>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}